import { useEffect, useRef, useState } from 'react';
import { HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import apiService from '../services/ApiService';
import resolveHubBaseUrl from '../utils/hubBaseUrl';

const CARD_FIELDS = [
  'totalErrores',
  'totalCriticos',
  'totalNoManejados',
  'totalManejados',
  'cantidadColegiosAfectados',
  'cantidadParticularesAfectados',
  'totalProblemasUnicos'
];
const FLASH_DURATION_MS = 2200;
const ALERT_DURATION_MS = 3200;
const REFRESH_DEBOUNCE_MS = 450;

const getHubBaseUrl = () =>
  resolveHubBaseUrl();

const normalizeGuid = (value) => (value || '').toString().trim().toLowerCase();

const buildAlertText = (payload) => {
  const level = (payload?.nivel || 'error').toUpperCase();
  const kind = payload?.exceptionType || payload?.mensaje || 'error';
  return `${level}: ${kind}`;
};

const getChangedFields = (previousData, nextData) => {
  if (!previousData || !nextData) return [];
  return CARD_FIELDS.filter((field) => Number(previousData?.[field] || 0) !== Number(nextData?.[field] || 0));
};

const useSystemErrorsRealtime = ({
  enabled,
  colegioId = '',
  data,
  loadData,
  shouldHandleEvent
}) => {
  const [highlightedCards, setHighlightedCards] = useState({});
  const [liveAlert, setLiveAlert] = useState(null);
  const dataRef = useRef(data);
  const loadDataRef = useRef(loadData);
  const shouldHandleEventRef = useRef(shouldHandleEvent);
  const connectionRef = useRef(null);
  const refreshTimerRef = useRef(null);
  const alertTimerRef = useRef(null);
  const flashTimersRef = useRef({});

  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  useEffect(() => {
    loadDataRef.current = loadData;
  }, [loadData]);

  useEffect(() => {
    shouldHandleEventRef.current = shouldHandleEvent;
  }, [shouldHandleEvent]);

  useEffect(() => {
    if (!enabled) return undefined;

    let disposed = false;
    const hubBase = getHubBaseUrl();
    const connection = new HubConnectionBuilder()
      .withUrl(`${hubBase}/hubs/metricas`, {
        accessTokenFactory: () => apiService.getTokenFromCookies() || '',
        withCredentials: true
      })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Warning)
      .build();

    const clearTimers = () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
      if (alertTimerRef.current) {
        clearTimeout(alertTimerRef.current);
        alertTimerRef.current = null;
      }
      Object.values(flashTimersRef.current).forEach((timerId) => clearTimeout(timerId));
      flashTimersRef.current = {};
    };

    const markChangedFields = (fields, payload) => {
      if (!fields.length) return;

      setHighlightedCards((prev) => {
        const next = { ...prev };
        fields.forEach((field) => {
          next[field] = true;
        });
        return next;
      });

      fields.forEach((field) => {
        if (flashTimersRef.current[field]) {
          clearTimeout(flashTimersRef.current[field]);
        }

        flashTimersRef.current[field] = setTimeout(() => {
          setHighlightedCards((prev) => ({ ...prev, [field]: false }));
          delete flashTimersRef.current[field];
        }, FLASH_DURATION_MS);
      });

      setLiveAlert({
        id: `${payload?.eventId || payload?.fechaUtc || Date.now()}`,
        text: buildAlertText(payload)
      });

      if (alertTimerRef.current) {
        clearTimeout(alertTimerRef.current);
      }

      alertTimerRef.current = setTimeout(() => {
        setLiveAlert(null);
      }, ALERT_DURATION_MS);
    };

    const queueRefresh = (payload) => {
      if (typeof shouldHandleEventRef.current === 'function' && !shouldHandleEventRef.current(payload)) {
        return;
      }

      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }

      refreshTimerRef.current = setTimeout(async () => {
        const previousData = dataRef.current;
        const nextData = await loadDataRef.current?.({
          showLoader: false,
          silentError: true
        });

        if (disposed || !nextData) return;

        const changedFields = getChangedFields(previousData, nextData);
        markChangedFields(changedFields, payload);
      }, REFRESH_DEBOUNCE_MS);
    };

    const joinGroups = async () => {
      await connection.invoke('JoinGlobalMetrics');
      if (normalizeGuid(colegioId)) {
        await connection.invoke('JoinColegioMetrics', colegioId);
      }
    };

    connection.on('SystemErrorsUpdated', queueRefresh);
    connection.onreconnected(async () => {
      try {
        await joinGroups();
      } catch (err) {
        console.warn('No se pudo reenganchar el hub de errores', err);
      }
    });

    (async () => {
      try {
        await connection.start();
        if (disposed) {
          await connection.stop();
          return;
        }
        await joinGroups();
        connectionRef.current = connection;
      } catch (err) {
        console.warn('No se pudo conectar al hub de errores', err);
      }
    })();

    return () => {
      disposed = true;
      clearTimers();
      const activeConnection = connectionRef.current || connection;
      activeConnection.stop().catch(() => {});
      connectionRef.current = null;
    };
  }, [enabled, colegioId]);

  return {
    highlightedCards,
    liveAlert
  };
};

export const errorsEventMatchesColegio = (payload, colegioId) =>
  !normalizeGuid(colegioId) || normalizeGuid(payload?.idColegio) === normalizeGuid(colegioId);

export const errorsEventMatchesParticular = (payload, profesorParticularId) =>
  !normalizeGuid(profesorParticularId) ||
  normalizeGuid(payload?.idProfesorParticular) === normalizeGuid(profesorParticularId);

export const errorsEventMatchesTenantType = (payload, tipoTenant) => {
  const normalized = (tipoTenant || 'todos').toString().trim().toLowerCase();
  if (!normalized || normalized === 'todos' || normalized === 'all') {
    return true;
  }

  if (normalized === 'colegios') {
    return Boolean(normalizeGuid(payload?.idColegio));
  }

  if (normalized === 'particulares') {
    return Boolean(normalizeGuid(payload?.idProfesorParticular));
  }

  return true;
};

export default useSystemErrorsRealtime;
